import { normalizeVehicleRegistration, type SharedDvlaVehicleLookupResult } from './home';

export const SHARED_SUPABASE_SESSION_HEADER = 'x-supabase-auth';

type SharedFunctionError = {
  message: string;
};

type InvokeSharedDvlaVehicleLookupOptions = {
  supabaseUrl: string | null | undefined;
  publishableKey: string | null | undefined;
  accessToken: string | null | undefined;
  registrationNumber: string;
  fetchImpl?: typeof fetch;
};

function buildFunctionErrorMessage(status: number, payload: unknown) {
  if (payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string') {
    return payload.error;
  }

  if (status === 401) {
    return 'You must be signed in to look up vehicle details.';
  }

  if (status === 404) {
    return 'DVLA could not find that registration number.';
  }

  return `DVLA lookup failed right now (${status}).`;
}

export async function invokeSharedDvlaVehicleLookup(
  options: InvokeSharedDvlaVehicleLookupOptions,
): Promise<{ data: SharedDvlaVehicleLookupResult | null; error: SharedFunctionError | null }> {
  const normalizedRegistration = normalizeVehicleRegistration(options.registrationNumber);

  if (!normalizedRegistration) {
    return {
      data: null,
      error: { message: 'Registration number is required.' },
    };
  }

  if (!options.supabaseUrl || !options.publishableKey) {
    return {
      data: null,
      error: { message: 'Supabase is not configured right now.' },
    };
  }

  if (!options.accessToken) {
    return {
      data: null,
      error: { message: 'You must be signed in to look up vehicle details.' },
    };
  }

  try {
    const response = await (options.fetchImpl ?? fetch)(`${options.supabaseUrl.replace(/\/+$/, '')}/functions/v1/vehicle-dvla-lookup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: options.publishableKey,
        [SHARED_SUPABASE_SESSION_HEADER]: options.accessToken,
      },
      body: JSON.stringify({
        registrationNumber: normalizedRegistration,
      }),
    });

    let payload: unknown = null;

    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      return {
        data: null,
        error: { message: buildFunctionErrorMessage(response.status, payload) },
      };
    }

    return {
      data: payload as SharedDvlaVehicleLookupResult,
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      error: {
        message: error instanceof Error ? error.message : 'DVLA lookup is unavailable right now.',
      },
    };
  }
}
